import {useTranslation} from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {Box, Typography} from '@mui/material';

import {recommendationCallout} from '../../../../../layouts/theme/designSystem';

type CalloutVariant = keyof typeof recommendationCallout;

const getCalloutVariant = (overall: number): CalloutVariant => {
  if (overall >= 80) return 'fit';
  if (overall >= 65) return 'watch';
  return 'risk';
};

export interface Props {
  overall: number;
  gapCount: number;
}

export const WorkforceProfileRecommendationCallout = ({overall, gapCount}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.recommendation'});
  const variant = getCalloutVariant(overall);
  const styles = recommendationCallout[variant];

  const icons: Record<CalloutVariant, React.ReactNode> = {
    fit: <CheckCircleOutlineIcon sx={{fontSize: 20}} />,
    watch: <WarningAmberIcon sx={{fontSize: 20}} />,
    risk: <ErrorOutlineIcon sx={{fontSize: 20}} />,
  };

  const titleKey: Record<CalloutVariant, string> = {
    fit: 'strongFitTitle',
    watch: 'acceptableFitTitle',
    risk: 'belowThresholdTitle',
  };

  const bodyKey: Record<CalloutVariant, string> = {
    fit: 'strongFitBody',
    watch: 'acceptableFitBody',
    risk: 'belowThresholdBody',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        py: 1.5,
        px: 2,
        borderRadius: '10px',
        bgcolor: styles.bgcolor,
        color: styles.color,
        border: `1px solid ${styles.borderColor}`,
        mb: 2,
      }}
    >
      <Box sx={{flexShrink: 0, mt: 0.125}}>{icons[variant]}</Box>
      <Box>
        <Typography sx={{fontSize: 13, fontWeight: 600, mb: 0.25, color: 'inherit'}}>{t(titleKey[variant])}</Typography>
        <Typography sx={{fontSize: 12.5, lineHeight: 1.5, color: 'inherit'}}>
          {t(bodyKey[variant], {gapCount})}
        </Typography>
      </Box>
    </Box>
  );
};
